import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NavigateFunction } from 'react-router-dom';
import progressMagicJwtToken, { twkToken1, twkToken2 } from '../../config/constants';
import { IChangeUserPasswordParams } from '../../service/api/authApi';
import { UserType } from '../../components/User/User';
import { IdentityProviderEnum, SubscriptionPlanTypes } from '../../types/coach';
import {
  removeBrandConfigurations,
  setBrandConfigurations,
} from '../../utils/brandConfigurationsUtils';
import { BookingTypeEnum } from '../booking/bookingSlice';
import { logout } from '../GlobalAction';
import { ICoachTypes } from '../onboarding/onboardingSlice';
import { Role } from '../coachGroup/coachGroupSlice';

export interface IUserPermissions {
  permissionCode: string;
}

export interface IModule {
  UserPermissions: IUserPermissions[];
}

export interface ISubscriptionPlan {
  id: string;
  name: string;
  maxUserCount: number;
}

export enum ClientLimitStatus {
  NOT_REACHED = 'NOT_REACHED',
  ABOUT_TO_REACH = 'ABOUT_TO_REACH',
  LIMIT_REACHED = 'LIMIT_REACHED',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export enum VideoCallPlatform {
  GOOGLE_MEET = 'GOOGLE_MEET',
  ZOOM = 'ZOOM',
}

export interface ISubscriptionPlanStatus {
  currentPlan: SubscriptionPlanTypes;
  paymentStatus: PaymentStatus;
  daysLeft?: number;
  nextPlan?: SubscriptionPlanTypes;
}

export interface ICoachConfiguartions {
  id: string;
  coachId: string;
  videoCallPlatform?: VideoCallPlatform;
  isProvideInPersonConsultations?: boolean;
  isProvideOnlineConsultations: boolean;
  activeStep: number;
}

export interface ICoach {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  surname: string;
  tp?: string;
  status: string;
  country: string;
  dob?: Date;
  pronoun: string;
  organizationId: string;
  archived: boolean;
  addressLine1: string;
  addressLine2: string;
  profilePic: string;
  timeZone: string;
  language?: string;
  clientCount?: number;
  isActive: boolean;
  isAgreedPrivacyPolicy: boolean;
  subscribeToNewsletter: boolean;
  isExternalImage?: boolean;
  userGroup: {
    modules: IModule[];
  };
  subscriptionPlan?: ISubscriptionPlan;
  paymentStatus?: PaymentStatus;
  previousPlan?: SubscriptionPlanTypes;
  activeClients?: number;
  inactiveClients?: number;
  archivedClients?: number;
  userStatusType?: string;
  subscriptionPlanName?: SubscriptionPlanTypes;
  subscriptionPlanStatus?: ISubscriptionPlanStatus;
  isReadOnly?: boolean;
  brandTag?: string | null;
  lastSeanTime?: string;
  coachTypes?: ICoachTypes[];
  role?: Role;
  isPrincipalCoach?: boolean;
  stripeStatus?: boolean;
  clientCountStatus?: ClientLimitStatus;
}

export interface IAdmin {
  firstName: string;
  lastName: string;
  email: string;
  profilePic: string;
}

export interface ICreateCoachInput {
  CreateCoach: {
    firstName: string;
    lastName: string;
    dob: string;
    pronoun: string;
    email: string;
    tp: string;
    organizationId: string;
    profilePic?: string;
    archived: boolean;
    addressLine1: string;
    addressLine2: string;
    timeZone: string;
    coachType: string;
    description: string;
  };
}

export interface IBrandConfigurations {
  id: string;
  brandTag: string;
  brandName: string;
  logo: string;
  brandNameLogo: string;
}
export interface ILoggedUser {
  username: string;
  userType: string;
  accessToken: {
    jwtToken: string;
  };
  refreshToken: {
    token: string;
  };
  userId: string;
  message?: string;
  brandConfigurations: IBrandConfigurations | null;
}

export interface IGoogleSignInUser {
  userType: string;
  accessToken: {
    jwtToken: string;
  };
  refreshToken: {
    token: string;
  };
  brandConfigurations: IBrandConfigurations | null;
}

export interface IFetchCoachesPayload {
  organizationId: string;
  nameText?: string;
  skip: number;
  take: number;
  status?: string;
  archived?: boolean;
  brandTag?: string;
}

export interface IUpdateCoachLanguagePayload {
  coachId: string;
  lang: string;
}

export enum CoachStatus {
  ENABLE = 'ENABLE',
  DISABLE = 'DISABLE',
}

export interface IChangeCoachStatusPayload {
  coachId: string;
  status: CoachStatus;
}

export interface IArchiveCoachPayload {
  coachId: string;
  password: string;
}

export interface IUpdateCoachPayload {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  tp?: string;
  timeZone: string;
  organizationId: string;
  profilePicFile: File | null;
}

export interface ICoachAISettings {
  id: string;
  isNoteTakingAIEnabled: boolean;
  noteSummaryAIModel: string;
  isMagicSuggestionEnabled: boolean;
  magicSuggestionAIModel: string;
}

export interface IUpdateCoachAISettings {
  id: string;
  isNoteTakingAIEnabled: boolean;
  isMagicSuggestionEnabled: boolean;
  noteSummaryAIModel: string;
  magicSuggestionAIModel: string;
}

export interface IGetBrandConfigurationsResponse {
  getBrandConfigurations: IBrandConfigurations;
}

export interface ICoachServicePackage {
  id: string;
  title: string;
  sessionPrice: number;
  sessionDuration: number;
  sessionDescription: string;
  coachId: string;
  bookingType: BookingTypeEnum;
  hasBookings?: boolean;
}

export interface ICreateServicePackageResponse {
  createServicePackage: ICoachServicePackage;
}

export interface IUpdateServicePackageResponse {
  updateServicePackage: ICoachServicePackage;
}

export interface IReturnCoachServicePackages {
  getCoachServicePackages: {
    servicePackages: ICoachServicePackage[];
  };
}

export interface ICreateServicePackageInput {
  title: string;
  bookingType: BookingTypeEnum;
  sessionPrice: number;
  sessionDuration: number;
  sessionDescription: string;
  coachId: string;
  activeStep?: number;
}

export interface IUpdateServicePackageInput {
  id: string;
  title: string;
  bookingType: BookingTypeEnum;
  sessionPrice: number;
  sessionDuration: number;
  sessionDescription: string;
  activeStep?: number;
}

export interface IEventDetails {
  id: string;
  coachId: string;
  bufferTime: number;
  minimumNoticeTime: number;
  dayLimit: number;
}

export interface IAddCoachEventSettingsInput {
  coachId: string;
  bufferTime: number;
  minimumNoticeTime: number;
  dayLimit: number;
}

export interface ICreatePasswordInput {
  token: string;
  password: string;
  confirmPassword: string;
}

interface ICoachState {
  totalCoachesCount: number;
  coach: ICoach;
  loading: boolean;
  error: string | null;
  loggedInStatus: boolean;
  languageUpdateStatus: 'PENDING' | 'SUCCESS' | 'FAILURE' | 'NONE';
  updatedLanguage: string | null;
  languageUpdateError: string | null;
  loadingLogin: boolean;
  role: UserType;
  coaches: ICoach[];
  coachesLoading: boolean;
  admin: IAdmin | null;
  changeCoachStatusLoading: boolean;
  changeCoachStatusError: string;
  statusChangedCoachId: string;
  totalArchivedCoachCount: number;
  archivedCoaches: ICoach[];
  archiveCoachLoading: boolean;
  archiveCoachSuccess: boolean;
  fetchArchivedCoachesLoading: boolean;
  updateCoachLoading: boolean;
  coachAISettings: ICoachAISettings;
  fetchCoachSettingsLoading: boolean;
  updateCoachAISettingsLoading: boolean;
  googleSignInLoading: boolean;
  redirectUrl: string | null;
  coachDataRequestSuccess: boolean;
  coachDataRequestLoading: boolean;
  coachDeleteRequestLoading: boolean;
  coachDeleteRequestSuccess: boolean;
  getBrandConfigurationsLoading: boolean;
  coachServicePackages: ICoachServicePackage[];
  coachServicePackagesLoading: boolean;
  coachServicePackagesError: string | null;
  coachEventDetails: IEventDetails;
  coachEventDetailsLoading: boolean;
  updateCoachEventLoading: boolean;
  getCoachServicePackagesSuccess: boolean;
  createCoachServicePackagesLoading: boolean;
  addingNewPackageSuccess: boolean;
  updateCoachServicePackageLoading: boolean;
  updatingNewPackageSuccess: boolean;
  deleteCoachServicePackageLoading: boolean;
  deletingPackageSuccess: boolean;
  createPasswordLoading: boolean;
}
const initialState: ICoachState = {
  totalCoachesCount: 0,
  languageUpdateStatus: 'NONE',
  languageUpdateError: null,
  updatedLanguage: null,
  coach: {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    surname: '',
    tp: '',
    status: '',
    country: '',
    pronoun: '',
    organizationId: '',
    archived: false,
    addressLine1: '',
    addressLine2: '',
    profilePic: '',
    timeZone: '',
    isActive: false,
    isAgreedPrivacyPolicy: false,
    subscribeToNewsletter: false,
    isExternalImage: false,
    userGroup: {
      modules: [],
    },
    paymentStatus: PaymentStatus.PENDING,
    activeClients: 0,
    inactiveClients: 0,
    archivedClients: 0,
    coachTypes: [],
  },
  loading: true,
  error: null,
  loggedInStatus: false,
  loadingLogin: false,
  role: UserType.COACH,
  coaches: [],
  coachesLoading: false,
  admin: null,
  changeCoachStatusLoading: false,
  changeCoachStatusError: '',
  statusChangedCoachId: '',
  totalArchivedCoachCount: 0,
  archivedCoaches: [],
  archiveCoachLoading: false,
  archiveCoachSuccess: false,
  fetchArchivedCoachesLoading: false,
  updateCoachLoading: false,
  coachAISettings: {
    isNoteTakingAIEnabled: false,
    noteSummaryAIModel: '',
    isMagicSuggestionEnabled: false,
    magicSuggestionAIModel: '',
    id: '',
  },
  updateCoachAISettingsLoading: false,
  fetchCoachSettingsLoading: false,
  googleSignInLoading: false,
  redirectUrl: null,
  coachDataRequestSuccess: false,
  coachDataRequestLoading: false,
  coachDeleteRequestLoading: false,
  coachDeleteRequestSuccess: false,
  getBrandConfigurationsLoading: false,
  coachServicePackages: [],
  coachServicePackagesLoading: false,
  coachServicePackagesError: null,
  coachEventDetails: {
    id: '',
    coachId: '',
    bufferTime: 0,
    minimumNoticeTime: 240,
    dayLimit: 0,
  },
  coachEventDetailsLoading: false,
  updateCoachEventLoading: false,
  getCoachServicePackagesSuccess: false,
  createCoachServicePackagesLoading: false,
  addingNewPackageSuccess: false,
  updateCoachServicePackageLoading: false,
  updatingNewPackageSuccess: false,
  deleteCoachServicePackageLoading: false,
  deletingPackageSuccess: false,
  createPasswordLoading: false,
};

const coachSlice = createSlice({
  name: 'coach',
  initialState,
  reducers: {
    fetchUpdateCoachLanguageSuccess: (state: ICoachState, action) => {
      state.languageUpdateStatus = 'SUCCESS';
      state.updatedLanguage = action.payload.language;
    },
    fetchUpdateCoachLanguageFailure: (state: ICoachState, action) => {
      state.languageUpdateStatus = 'FAILURE';
      state.languageUpdateError = action.payload;
    },
    fetchUpdateCoachLanguageRequest: (
      state: ICoachState,
      _action: PayloadAction<IUpdateCoachLanguagePayload>
    ) => {
      state.languageUpdateStatus = 'PENDING';
    },
    fetchCoachRequest: (state: ICoachState) => {
      state.loading = true;
    },
    languageUpdate: (state: ICoachState, action) => {
      state.updatedLanguage = action.payload.language;
    },
    fetchCoachSuccess: (state: ICoachState, action) => {
      state.loading = false;
      state.coach = action.payload;
      state.updatedLanguage = action.payload.language;
      state.loggedInStatus = true;
    },
    setAdminLoginSuccess: (state: ICoachState) => {
      state.loading = false;
      // can add dummy data here
      state.admin = {
        firstName: 'ProgressMagic',
        lastName: 'Admin',
        email: 'admin@progressmagic.com',
        // dummy profile pic here
        profilePic: 'media/coaches/f3db966a-563d-43c2-a8c4-a8ef4ed30cf7/images/profile-pic.jpeg',
      };
      state.loggedInStatus = true;
      state.role = UserType.ADMIN;
    },
    fetchCoachFailure: (state: ICoachState, action) => {
      state.loading = false;
      state.error = action.payload;
      state.loggedInStatus = false;
    },
    fetchCoachesRequest: (state: ICoachState, _action: PayloadAction<IFetchCoachesPayload>) => {
      state.coachesLoading = true;
    },
    fetchCoachesSuccess: (state: ICoachState, action) => {
      state.coachesLoading = false;
      state.coaches = action.payload.coaches;
      state.totalCoachesCount = action.payload.totalCoachesCount;
    },
    fetchCoachesFailure: (state: ICoachState, action) => {
      state.coachesLoading = false;
      state.error = action.payload;
    },
    coachLoginRequest: (
      state: ICoachState,
      _action: PayloadAction<{ username: string; password: string }>
    ) => {
      state.loadingLogin = true;
    },
    coachLoginSuccess: (state: ICoachState, action: PayloadAction<ILoggedUser>) => {
      const { jwtToken } = action.payload.accessToken;
      // store jwtToken in local storage
      localStorage.setItem(progressMagicJwtToken, jwtToken);
      state.role = action.payload.userType as UserType;
      state.loadingLogin = false;
      state.loggedInStatus = true;
      if (action.payload.brandConfigurations) {
        const { brandTag, brandNameLogo, logo } = action.payload.brandConfigurations;
        setBrandConfigurations(brandTag, brandNameLogo, logo);
      } else {
        removeBrandConfigurations();
      }
    },
    coachLoginFailure: (state: ICoachState, action) => {
      state.loadingLogin = false;
      state.error = action.payload;
      state.loggedInStatus = false;
      state.loading = false;
    },
    setCoachIsLoggedIn: (state: ICoachState, action) => {
      state.loggedInStatus = action.payload;
      state.loading = false;
    },
    passwordResetRequest: (
      state: ICoachState,
      _action: PayloadAction<IChangeUserPasswordParams>
    ) => {
      state.loading = true;
    },
    passwordResetSuccess: (state: ICoachState) => {
      state.loading = false;
    },
    passwordResetFailure: (state: ICoachState) => {
      state.loading = false;
    },
    approveCoachRequest: (state: ICoachState, _action: PayloadAction<{ coachId: string }>) => {
      state.loading = true;
    },
    approveCoachSuccess: (state: ICoachState) => {
      state.loading = false;
    },
    approveCoachFailure: (state: ICoachState) => {
      state.loading = false;
    },
    rejectCoachRequest: (state: ICoachState, _action: PayloadAction<{ coachId: string }>) => {
      state.loading = true;
    },
    rejectCoachSuccess: (state: ICoachState) => {
      state.loading = false;
    },
    rejectCoachFailure: (state: ICoachState) => {
      state.loading = false;
    },
    addCoachRequest: (state: ICoachState, _action: PayloadAction<ICreateCoachInput>) => {
      state.loading = true;
    },
    addCoachSuccess: (state: ICoachState, _action: PayloadAction<ICoach>) => {
      state.loading = false;
    },
    addCoachFailure: (state: ICoachState, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    changeCoachStatus: (state, action: PayloadAction<IChangeCoachStatusPayload>) => {
      state.changeCoachStatusLoading = true;
      state.statusChangedCoachId = action.payload.coachId;
    },
    changeCoachStatusSuccess: (state, action: PayloadAction<boolean>) => {
      const coachToUpdate = state.coaches.find((v) => v.id === state.statusChangedCoachId);
      if (coachToUpdate) {
        coachToUpdate.isActive = action.payload;
        state.statusChangedCoachId = '';
        state.changeCoachStatusLoading = false;
      }
    },
    changeCoachStatusFailure: (state, action: PayloadAction<string>) => {
      state.changeCoachStatusLoading = false;
      state.changeCoachStatusError = action.payload;
    },
    archiveCoachRequest: (state: ICoachState, _action: PayloadAction<IArchiveCoachPayload>) => {
      state.archiveCoachLoading = true;
      state.archiveCoachSuccess = false;
    },
    archiveCoachSuccess: (state: ICoachState, _action) => {
      state.archiveCoachLoading = false;
      state.archiveCoachSuccess = true;
    },
    closeArchieveModal: (state: ICoachState) => {
      state.archiveCoachSuccess = false;
    },
    archiveCoachFailure: (state: ICoachState, action) => {
      state.archiveCoachLoading = false;
      state.error = action.payload;
    },
    fetchArchivedCoaches: (state: ICoachState, _action: PayloadAction<IFetchCoachesPayload>) => {
      state.fetchArchivedCoachesLoading = true;
    },
    fetchArchivedCoachesSuccess: (state: ICoachState, action) => {
      state.fetchArchivedCoachesLoading = false;
      state.archivedCoaches = action.payload.coaches;
      state.totalArchivedCoachCount = action.payload.totalCoachesCount;
    },
    fetchArchivedCoachesFailure: (state: ICoachState, action) => {
      state.fetchArchivedCoachesLoading = false;
      state.error = action.payload;
    },
    updateCoach: (state, _action: PayloadAction<IUpdateCoachPayload>) => {
      state.updateCoachLoading = true;
    },
    updateCoachSuccess: (state) => {
      state.updateCoachLoading = false;
    },
    updateCoachFailure: (state, action) => {
      state.updateCoachLoading = false;
      state.error = action.payload;
    },
    fetchCoachSettings: (state, _action: PayloadAction<string>) => {
      state.fetchCoachSettingsLoading = true;
    },
    fetchCoachSettingsSuccess: (state, action) => {
      state.fetchCoachSettingsLoading = false;
      state.coachAISettings = action.payload;
    },
    fetchCoachSettingsFailure: (state, action) => {
      state.fetchCoachSettingsLoading = false;
      state.error = action.payload;
    },
    updateCoachSettings: (state, _action: PayloadAction<IUpdateCoachAISettings>) => {
      state.updateCoachAISettingsLoading = true;
    },
    updateCoachSettingsSuccess: (state, action: PayloadAction<IUpdateCoachAISettings>) => {
      state.updateCoachAISettingsLoading = false;
      state.coachAISettings = action.payload;
    },
    updateCoachSettingsFailure: (state, action) => {
      state.updateCoachAISettingsLoading = false;
      state.error = action.payload;
    },
    getSocialSignInURL: (
      _state: ICoachState,
      _action: PayloadAction<{ identityProvider: IdentityProviderEnum }>
    ) => {},
    getSocialSignInURLSuccess: (state: ICoachState, action: PayloadAction<string>) => {
      state.redirectUrl = action.payload;
    },
    getSocialSignInURLFailure: (_state: ICoachState) => {},
    socialSignInRequest: (
      state: ICoachState,
      _action: PayloadAction<{ code: string; currentLocation?: string; brandTag?: string }>
    ) => {
      state.loadingLogin = true;
    },
    socialSignInSuccess: (state: ICoachState, action: PayloadAction<IGoogleSignInUser>) => {
      const { jwtToken } = action.payload.accessToken;
      // store jwtToken in local storage
      localStorage.setItem(progressMagicJwtToken, jwtToken);
      state.role = action.payload.userType as UserType;
      state.loadingLogin = false;
      state.loggedInStatus = true;
      if (action.payload.brandConfigurations) {
        const { brandTag, brandNameLogo, logo } = action.payload.brandConfigurations;
        setBrandConfigurations(brandTag, brandNameLogo, logo);
      } else {
        removeBrandConfigurations();
      }
    },
    socialSignInFailure: (state: ICoachState, action) => {
      state.loadingLogin = false;
      state.error = action.payload;
      state.loggedInStatus = false;
      state.loading = false;
    },
    coachDataRequest: (state: ICoachState, _action: PayloadAction<{ coachId: string }>) => {
      state.coachDataRequestLoading = true;
    },
    coachDataRequestSuccess: (state: ICoachState) => {
      state.coachDataRequestLoading = false;
      state.coachDataRequestSuccess = true;
    },
    closeDataRequestSuccessModal: (state: ICoachState) => {
      state.coachDataRequestSuccess = false;
    },
    coachDataRequestFailure: (state: ICoachState, action) => {
      state.coachDataRequestLoading = false;
      state.error = action.payload;
    },
    deleteCoachProfilePicture: (state: ICoachState) => {
      state.coach.profilePic = 'null';
    },
    coachDeleteRequest: (
      state: ICoachState,
      _action: PayloadAction<{ coachId: string; password?: string }>
    ) => {
      state.coachDeleteRequestLoading = true;
    },
    coachDeleteRequestSuccess: (state: ICoachState) => {
      state.coachDeleteRequestLoading = false;
      state.coachDeleteRequestSuccess = true;
    },
    resetCoachDeleteRequestSuccess: (state: ICoachState) => {
      state.coachDeleteRequestSuccess = false;
    },
    coachDeleteRequestFailure: (state: ICoachState, action) => {
      state.coachDeleteRequestLoading = false;
      state.error = action.payload;
    },
    updateSubscriptionPlanStatus: (
      state: ICoachState,
      action: PayloadAction<{
        subscriptionPlanStatus: ISubscriptionPlanStatus | undefined;
        clientCountStatus: ClientLimitStatus | undefined;
      }>
    ) => {
      state.coach.subscriptionPlanStatus =
        action.payload.subscriptionPlanStatus || state.coach.subscriptionPlanStatus;
      state.coach.clientCountStatus =
        action.payload.clientCountStatus || state.coach.clientCountStatus;
    },
    coachDowngradePlanSuccess: (state: ICoachState) => {
      state.coach.paymentStatus = PaymentStatus.PENDING;
    },
    updateSubscriptionPlanName: (state) => {
      state.coach.subscriptionPlanName = SubscriptionPlanTypes.PRO; // TODO: Change this to the actual plan
    },
    getBrandConfigurations: (
      state: ICoachState,
      _action: PayloadAction<{ brandTag: string; navigate: NavigateFunction }>
    ) => {
      state.getBrandConfigurationsLoading = true;
    },
    getBrandConfigurationsSuccess: (
      state: ICoachState,
      action: PayloadAction<IBrandConfigurations>
    ) => {
      state.getBrandConfigurationsLoading = false;
      const { brandTag, brandNameLogo, logo } = action.payload;
      setBrandConfigurations(brandTag, brandNameLogo, logo);
    },
    getBrandConfigurationsFailure: (state: ICoachState) => {
      state.getBrandConfigurationsLoading = false;
    },
    getCoachServicePackagesRequest: (
      state: ICoachState,
      _action: PayloadAction<{ coachId: string }>
    ) => {
      state.coachServicePackagesLoading = true;
    },
    getCoachServicePackagesSuccess: (
      state: ICoachState,
      action: PayloadAction<ICoachServicePackage[]>
    ) => {
      state.coachServicePackagesLoading = false;
      state.coachServicePackages = action.payload;
      state.getCoachServicePackagesSuccess = true;
    },
    getCoachServicePackagesFailure: (state: ICoachState, action: PayloadAction<string>) => {
      state.coachServicePackagesLoading = false;
      state.coachServicePackagesError = action.payload;
      state.getCoachServicePackagesSuccess = false;
    },
    resetCoachServicePackages(state: ICoachState) {
      state.getCoachServicePackagesSuccess = false;
      state.addingNewPackageSuccess = false;
      state.updatingNewPackageSuccess = false;
      state.deletingPackageSuccess = false;
    },
    createCoachServicePackageRequest: (
      state: ICoachState,
      _action: PayloadAction<ICreateServicePackageInput>
    ) => {
      state.createCoachServicePackagesLoading = true;
    },
    createCoachServicePackageSuccess: (
      state: ICoachState,
      action: PayloadAction<ICoachServicePackage>
    ) => {
      state.createCoachServicePackagesLoading = false;
      // push it to first index
      state.coachServicePackages = [action.payload, ...state.coachServicePackages];
      state.addingNewPackageSuccess = true;
    },
    createCoachServicePackageFailure: (state: ICoachState, action: PayloadAction<string>) => {
      state.createCoachServicePackagesLoading = false;
      state.coachServicePackagesError = action.payload;
      state.addingNewPackageSuccess = false;
    },
    updateCoachServicePackageRequest: (
      state: ICoachState,
      _action: PayloadAction<IUpdateServicePackageInput>
    ) => {
      state.updateCoachServicePackageLoading = true;
    },
    updateCoachServicePackageSuccess: (
      state: ICoachState,
      action: PayloadAction<ICoachServicePackage>
    ) => {
      state.updateCoachServicePackageLoading = false;
      const index = state.coachServicePackages.findIndex((pkg) => pkg.id === action.payload.id);
      if (index !== -1) {
        state.coachServicePackages[index] = {
          ...state.coachServicePackages[index],
          ...action.payload,
        };
        state.updatingNewPackageSuccess = true;
      }
    },
    updateCoachServicePackageFailure: (state: ICoachState, action: PayloadAction<string>) => {
      state.updateCoachServicePackageLoading = false;
      state.coachServicePackagesError = action.payload;
      state.updatingNewPackageSuccess = false;
    },
    deleteCoachServicePackageRequest: (
      state: ICoachState,
      _action: PayloadAction<{ id: string; coachId: string }>
    ) => {
      state.deleteCoachServicePackageLoading = true;
    },
    deleteCoachServicePackageSuccess: (state: ICoachState, action: PayloadAction<string>) => {
      state.deleteCoachServicePackageLoading = false;
      state.coachServicePackages = state.coachServicePackages.filter(
        (pkg) => pkg.id !== action.payload
      );
      state.deletingPackageSuccess = true;
    },
    deleteCoachServicePackageFailure: (state: ICoachState, action: PayloadAction<string>) => {
      state.deleteCoachServicePackageLoading = false;
      state.coachServicePackagesError = action.payload;
      state.deletingPackageSuccess = false;
    },
    getCoachEventDetailsRequest: (
      state: ICoachState,
      _action: PayloadAction<{ coachId: string }>
    ) => {
      state.coachEventDetailsLoading = true;
    },
    getCoachEventDetailsSuccess: (state: ICoachState, action: PayloadAction<IEventDetails>) => {
      state.coachEventDetailsLoading = false;
      state.coachEventDetails = action.payload;
    },
    getCoachEventDetailsFailure: (state: ICoachState, action: PayloadAction<string>) => {
      state.coachEventDetailsLoading = false;
      state.error = action.payload;
    },
    updateCoachEventDetailsRequest: (
      state: ICoachState,
      _action: PayloadAction<IAddCoachEventSettingsInput>
    ) => {
      state.updateCoachEventLoading = true;
    },
    updateCoachEventDetailsSuccess: (state: ICoachState, action: PayloadAction<IEventDetails>) => {
      state.updateCoachEventLoading = false;
      state.coachEventDetails = action.payload;
    },
    updateCoachEventDetailsFailure: (state: ICoachState, action: PayloadAction<string>) => {
      state.updateCoachEventLoading = false;
      state.error = action.payload;
    },
    createPasswordRequest: (state: ICoachState, _action: PayloadAction<ICreatePasswordInput>) => {
      state.createPasswordLoading = true;
    },
    createPasswordSuccess: (state: ICoachState) => {
      state.createPasswordLoading = false;
    },
    createPasswordFailure: (state: ICoachState) => {
      state.createPasswordLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.coach = {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        surname: '',
        tp: '',
        status: '',
        country: '',
        pronoun: '',
        organizationId: '',
        archived: false,
        addressLine1: '',
        addressLine2: '',
        profilePic: '',
        timeZone: '',
        isActive: false,
        isAgreedPrivacyPolicy: false,
        subscribeToNewsletter: false,
        userGroup: {
          modules: [],
        },
        brandTag: '',
        activeClients: 0,
        inactiveClients: 0,
        archivedClients: 0,
        subscriptionPlanStatus: undefined,
        coachTypes: [],
      };
      state.admin = null;
      state.loggedInStatus = false;
      state.loading = false;
      localStorage.removeItem(progressMagicJwtToken);
      localStorage.removeItem(twkToken1);
      localStorage.removeItem(twkToken2);
      state.coachServicePackages = [];
      state.coachServicePackagesError = null;
      state.coachServicePackagesLoading = false;
      state.createPasswordLoading = false;
      state.coachAISettings = {
        id: '',
        isMagicSuggestionEnabled: false,
        isNoteTakingAIEnabled: false,
        magicSuggestionAIModel: '',
        noteSummaryAIModel: '',
      };
    });
  },
});

export const {
  languageUpdate,
  fetchUpdateCoachLanguageSuccess,
  fetchUpdateCoachLanguageFailure,
  fetchUpdateCoachLanguageRequest,
  fetchCoachRequest,
  fetchCoachSuccess,
  fetchCoachFailure,
  coachLoginRequest,
  coachLoginSuccess,
  coachLoginFailure,
  setCoachIsLoggedIn,
  passwordResetRequest,
  passwordResetSuccess,
  passwordResetFailure,
  fetchCoachesRequest,
  fetchCoachesSuccess,
  fetchCoachesFailure,
  approveCoachRequest,
  approveCoachSuccess,
  approveCoachFailure,
  rejectCoachRequest,
  rejectCoachSuccess,
  rejectCoachFailure,
  addCoachRequest,
  addCoachSuccess,
  addCoachFailure,
  setAdminLoginSuccess,
  changeCoachStatus,
  changeCoachStatusSuccess,
  changeCoachStatusFailure,
  archiveCoachRequest,
  archiveCoachSuccess,
  archiveCoachFailure,
  closeArchieveModal,
  fetchArchivedCoaches,
  fetchArchivedCoachesSuccess,
  fetchArchivedCoachesFailure,
  updateCoach,
  updateCoachSuccess,
  updateCoachFailure,
  fetchCoachSettings,
  fetchCoachSettingsSuccess,
  fetchCoachSettingsFailure,
  updateCoachSettings,
  updateCoachSettingsSuccess,
  updateCoachSettingsFailure,
  getSocialSignInURL,
  getSocialSignInURLSuccess,
  getSocialSignInURLFailure,
  socialSignInRequest,
  socialSignInSuccess,
  socialSignInFailure,
  coachDataRequest,
  closeDataRequestSuccessModal,
  coachDataRequestFailure,
  coachDataRequestSuccess,
  deleteCoachProfilePicture,
  coachDeleteRequest,
  coachDeleteRequestSuccess,
  coachDeleteRequestFailure,
  resetCoachDeleteRequestSuccess,
  updateSubscriptionPlanStatus,
  coachDowngradePlanSuccess,
  updateSubscriptionPlanName,
  getBrandConfigurations,
  getBrandConfigurationsSuccess,
  getBrandConfigurationsFailure,
  getCoachServicePackagesRequest,
  getCoachServicePackagesSuccess,
  getCoachServicePackagesFailure,
  createCoachServicePackageRequest,
  createCoachServicePackageSuccess,
  createCoachServicePackageFailure,
  updateCoachServicePackageRequest,
  updateCoachServicePackageSuccess,
  updateCoachServicePackageFailure,
  deleteCoachServicePackageRequest,
  deleteCoachServicePackageSuccess,
  deleteCoachServicePackageFailure,
  getCoachEventDetailsRequest,
  getCoachEventDetailsSuccess,
  getCoachEventDetailsFailure,
  updateCoachEventDetailsRequest,
  updateCoachEventDetailsSuccess,
  updateCoachEventDetailsFailure,
  resetCoachServicePackages,
  createPasswordFailure,
  createPasswordRequest,
  createPasswordSuccess,
} = coachSlice.actions;

export default coachSlice.reducer;
